const WHITESPACE = /\s+/g;
const ONLY_LEADER_DOTS = /^\.*$/;

/**
 * The description text of a menu row wrapped as the browser last wrapped it, so the high-res render
 * breaks lines where the user saw them. Wrapping is taken from the saved `wrappingInfo` only when its
 * entry for the row is a list of lines that spell this very text; a row whose saved lines are missing,
 * malformed or describe an older text (rows added, removed or rewritten since) wraps naturally instead.
 *
 * Layouts that run leader dots from the description to the price (MenuLayout2) append the dots to the
 * description itself, so its saved lines end with them. Those lines still match, and keep their dots:
 * the dots are what stretch a short description to the width the user saw.
 */
export const getSavedWrapping = (wrappingInfo: string[][] | null, row: number, text: string): string => {
  const savedLines: unknown = wrappingInfo?.[row];
  if (!isListOfLines(savedLines) || !doLinesSpellTextWithLeaderDots(savedLines, text)) {
    return text;
  }
  return savedLines.join('\n');
};

const isListOfLines = (value: unknown): value is string[] => {
  return Array.isArray(value) && value.every((line) => typeof line === 'string');
};

/**
 * Whether the lines are the text, optionally followed by leader dots. Wrapping only moves the breaks
 * between words, so the lines and the text agree once whitespace is ignored.
 */
const doLinesSpellTextWithLeaderDots = (lines: string[], text: string): boolean => {
  const savedCharacters = lines.join('').replace(WHITESPACE, '');
  const textCharacters = text.replace(WHITESPACE, '');
  return savedCharacters.startsWith(textCharacters) && ONLY_LEADER_DOTS.test(savedCharacters.slice(textCharacters.length));
};
